import { useOnOutClick } from 'hooks';
import React, { useRef, useState } from 'react';
import ActionButton from './ActionButton';
import classes from './index.module.scss';
import { InvoicePaymentStatus, InvoicePaymentStatusType } from 'lib/util';

/**
 * Props interface for the PaymentStatusDropdown component
 */
interface Props {
	/** Callback function when a payment status is selected */
	onSelectStatus: (status: InvoicePaymentStatusType) => any;
	/** The current payment status of the invoice */
	currentStatus: InvoicePaymentStatusType;
}

/**
 * PaymentStatusDropdown component
 * Dropdown menu for changing the payment status of an invoice
 * Displays the current status and allows selecting a new status
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const PaymentStatusDropdown = ({ onSelectStatus, currentStatus }: Props) => {
	// State to track if dropdown is open or closed
	const [isOpen, setIsOpen] = useState(false);
	// Reference to the dropdown container for click outside detection
	const ref = useRef<HTMLDivElement>(null);

	// Helper functions for dropdown state management
	const closeDropdown = () => setIsOpen(false);
	const toggleDropdown = () => setIsOpen((prev) => !prev);

	// Close dropdown when clicking outside
	useOnOutClick({ ref, onOutClick: closeDropdown });

	/**
	 * Handle selection of a payment status
	 * Calls the onSelectStatus callback and closes the dropdown
	 *
	 * @param {InvoicePaymentStatusType} status - The selected payment status
	 */
	const handleSelect = (status: InvoicePaymentStatusType) => {
		onSelectStatus(status);
		closeDropdown();
	};

	return (
		<div ref={ref} className={classes['paymentStatusDropdown-container']}>
			{/* Button that shows current status and toggles dropdown */}
			<ActionButton
				onClick={toggleDropdown}
				title={InvoicePaymentStatus.text(currentStatus)}
				status={InvoicePaymentStatus.color(currentStatus)}
				rightIconSrc="icDownCaret"
			/>
			{/* Dropdown menu with status options */}
			{isOpen && (
				<ul className={classes['paymentStatusDropdown-list']}>
					{/* Paid status option */}
					<li className={classes['paymentStatusDropdown-listItem']}>
						<a onClick={() => handleSelect(InvoicePaymentStatusType.PAID)}>
							{InvoicePaymentStatus.text(InvoicePaymentStatusType.PAID)}
						</a>
					</li>
					{/* Unpaid status option */}
					<li className={classes['paymentStatusDropdown-listItem']}>
						<a onClick={() => handleSelect(InvoicePaymentStatusType.UNPAID)}>
							{InvoicePaymentStatus.text(InvoicePaymentStatusType.UNPAID)}
						</a>
					</li>
					{/* Refunded status option */}
					<li className={classes['paymentStatusDropdown-listItem']}>
						<a onClick={() => handleSelect(InvoicePaymentStatusType.REFUNDED)}>
							{InvoicePaymentStatus.text(InvoicePaymentStatusType.REFUNDED)}
						</a>
					</li>
				</ul>
			)}
		</div>
	);
};

export default PaymentStatusDropdown;
